import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import PropertyMap from '../components/PropertyMap';
import { useSavedProperties } from '../hooks/useSavedProperties';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Search, SlidersHorizontal, X, ChevronLeft, ChevronRight, Loader2, Map, LayoutGrid, LocateFixed } from 'lucide-react';
import { trackEvent } from '../utils/track';

const API = '/api';

const PROPERTY_TYPES = {
  residential: ['apartment', 'villa', 'duplex', 'triplex', 'farmhouse'],
  commercial: ['office', 'shop', 'showroom', 'godown'],
  industrial: ['industrial_shed', 'open_land'],
  pg: ['pg']
};

const BHK_OPTIONS = ['1 RK', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '4+ BHK'];
const FURNISHING_OPTIONS = ['furnished', 'semi-furnished', 'unfurnished'];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSaved, toggleSave } = useSavedProperties();

  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [geoLoading, setGeoLoading] = useState(false);

  const [filters, setFilters] = useState({
    city: params.get('city') || '',
    area: params.get('area') || '',
    pincode: params.get('pincode') || '',
    property_category: params.get('property_category') || '',
    property_type: params.get('property_type') || '',
    bhk_type: params.get('bhk_type') || '',
    furnishing: params.get('furnishing') || '',
    min_rent: params.get('min_rent') || '',
    max_rent: params.get('max_rent') || '',
    bachelor_allowed: params.get('bachelor_allowed') || '',
    pets_allowed: params.get('pets_allowed') || '',
    sort: params.get('sort') || 'newest',
  });

  const fetchProperties = useCallback(async (currentPage = 1) => {
    setLoading(true);
    const q = new URLSearchParams();
    q.set('page', currentPage);
    q.set('limit', 12);
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    try {
      const res = await fetch(`${API}/properties?${q.toString()}`);
      const data = await res.json();
      setProperties(data.properties || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(currentPage);
      // Track search with active filter context
      const active = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      if (Object.keys(active).length > 0) {
        trackEvent('search', {
          city: filters.city || '',
          category: filters.property_category || '',
          property_type: filters.property_type || '',
          min_rent: filters.min_rent || '',
          max_rent: filters.max_rent || '',
        });
      }
    } catch (err) { toast.error('Failed to load properties'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { fetchProperties(1); }, [fetchProperties]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ city: '', area: '', pincode: '', property_category: '', property_type: '', bhk_type: '', furnishing: '', min_rent: '', max_rent: '', bachelor_allowed: '', pets_allowed: '', sort: 'newest' });
  };

  const handleGeolocate = () => {
    if (!navigator.geolocation) { return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { 'User-Agent': 'RentMitra/1.0' } }
          );
          const data = await res.json();
          const city = data.address?.city || data.address?.town || data.address?.village || '';
          const area = data.address?.suburb || data.address?.neighbourhood || '';
          if (city) {
            setFilters(prev => ({ ...prev, city, area: area || prev.area }));
          }
        } catch (_err) { console.error('Geolocation reverse lookup failed (non-critical):', _err); }
        setGeoLoading(false);
      },
      () => setGeoLoading(false)
    );
  };

  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => v && k !== 'sort').length;

  const handleToggleSave = async (propertyId) => {
    if (!user) { navigate('/login'); return; }
    try {
      const saved = await toggleSave(propertyId);
      toast.success(saved ? 'Saved to favorites!' : 'Removed from saved');
    } catch {
      toast.error('Please login to save properties');
    }
  };

  const FilterPanel = () => (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">City</label>
          <button
            onClick={handleGeolocate}
            disabled={geoLoading}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors disabled:opacity-50"
            data-testid="geolocate-btn"
            title="Use my location"
          >
            <LocateFixed className={`w-3.5 h-3.5 ${geoLoading ? 'animate-pulse' : ''}`} />
            {geoLoading ? 'Locating...' : 'Near Me'}
          </button>
        </div>
        <input
          type="text" value={filters.city}
          onChange={e => updateFilter('city', e.target.value)}
          placeholder="e.g. Mumbai, Bangalore"
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500"
          data-testid="filter-city"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Area / Locality</label>
        <input
          type="text" value={filters.area}
          onChange={e => updateFilter('area', e.target.value)}
          placeholder="e.g. Andheri, Koramangala"
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500"
          data-testid="filter-area"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Pincode</label>
        <input
          type="text" value={filters.pincode}
          onChange={e => updateFilter('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="e.g. 400001"
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 font-mono"
          data-testid="filter-pincode"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Category</label>
        <select value={filters.property_category} onChange={e => {
          const cat = e.target.value;
          updateFilter('property_category', cat);
          updateFilter('property_type', '');
          // Clear irrelevant filters when switching to non-residential/pg
          if (cat && cat !== 'residential' && cat !== 'pg') {
            updateFilter('bhk_type', '');
            updateFilter('bachelor_allowed', '');
            updateFilter('pets_allowed', '');
          }
          if (cat === 'industrial') {
            updateFilter('furnishing', '');
          }
        }}
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 bg-white" data-testid="filter-category">
          <option value="">All Categories</option>
          <option value="residential">Residential</option>
          <option value="commercial">Commercial</option>
          <option value="industrial">Industrial</option>
          <option value="pg">PG / Hostel</option>
        </select>
      </div>
      {filters.property_category && PROPERTY_TYPES[filters.property_category] && (
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Property Type</label>
          <select value={filters.property_type} onChange={e => updateFilter('property_type', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 bg-white" data-testid="filter-type">
            <option value="">All Types</option>
            {PROPERTY_TYPES[filters.property_category].map(t => (
              <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
            ))}
          </select>
        </div>
      )}
      {/* BHK — only for Residential and PG */}
      {(!filters.property_category || filters.property_category === 'residential' || filters.property_category === 'pg') && (
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">BHK</label>
          <div className="flex flex-wrap gap-2">
            {BHK_OPTIONS.map(b => (
              <button key={b} onClick={() => updateFilter('bhk_type', filters.bhk_type === b ? '' : b)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${filters.bhk_type === b ? 'bg-blue-700 text-white border-blue-700' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
                data-testid={`filter-bhk-${b.replace(' ', '-')}`}>
                {b}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Rent Range (₹/month)</label>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" value={filters.min_rent} onChange={e => updateFilter('min_rent', e.target.value)}
            placeholder="Min" className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500" data-testid="filter-min-rent" />
          <input type="number" value={filters.max_rent} onChange={e => updateFilter('max_rent', e.target.value)}
            placeholder="Max" className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500" data-testid="filter-max-rent" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Furnishing</label>
        <div className="space-y-1.5">
          {FURNISHING_OPTIONS.map(f => (
            <label key={f} className="flex items-center gap-2.5 cursor-pointer">
              <input type="radio" name="furnishing" value={f} checked={filters.furnishing === f}
                onChange={e => updateFilter('furnishing', e.target.value)} className="text-blue-600" data-testid={`filter-furnishing-${f}`} />
              <span className="text-sm text-slate-700 capitalize">{f}</span>
            </label>
          ))}
          {filters.furnishing && (
            <button onClick={() => updateFilter('furnishing', '')} className="text-xs text-blue-600 hover:underline">Clear</button>
          )}
        </div>
      </div>
      {/* Bachelor & Pets — only for Residential */}
      {(!filters.property_category || filters.property_category === 'residential') && (
        <>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={filters.bachelor_allowed === 'true'} data-testid="filter-bachelor"
                onChange={e => updateFilter('bachelor_allowed', e.target.checked ? 'true' : '')} className="rounded text-blue-600" />
              <span className="text-sm text-slate-700">Bachelor Allowed</span>
            </label>
          </div>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={filters.pets_allowed === 'true'} data-testid="filter-pets"
                onChange={e => updateFilter('pets_allowed', e.target.checked ? 'true' : '')} className="rounded text-blue-600" />
              <span className="text-sm text-slate-700">Pets Allowed</span>
            </label>
          </div>
        </>
      )}
      {activeFiltersCount > 0 && (
        <button onClick={clearFilters} className="w-full py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl border border-red-200 transition-colors flex items-center justify-center gap-1.5" data-testid="clear-filters">
          <X className="w-3.5 h-3.5" />Clear All Filters
        </button>
      )}
    </div>
  );

  const seoTitle = (() => {
    const parts = [];
    if (filters.bhk_type) parts.push(filters.bhk_type);
    if (filters.property_category === 'pg') parts.push('PG / Hostel');
    else if (filters.property_type) parts.push(filters.property_type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()));
    else if (filters.property_category) parts.push(filters.property_category.replace(/\b\w/g, c => c.toUpperCase()));
    else parts.push('Rental Properties');
    const thing = parts.join(' ');
    const loc = filters.city || filters.area || '';
    return loc ? `${thing} for Rent in ${loc} | Rent Mitra` : `${thing} for Rent in India | Rent Mitra`;
  })();

  const seoDesc = `Find ${[filters.bhk_type, filters.property_category || 'rental', 'properties'].filter(Boolean).join(' ')}${filters.city ? ` in ${filters.city}` : ' across India'} on Rent Mitra. Verified listings, direct owner contact, no brokerage.`;

  const canonicalUrl = `${window.location.origin}/search${filters.city ? `?city=${encodeURIComponent(filters.city)}` : ''}`;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rent Mitra" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
      </Helmet>
      <Navbar />
      {/* Search Header */}
      <div className="bg-slate-50 border-b border-slate-200 py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-2.5 shadow-sm">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text" value={filters.city}
                onChange={e => updateFilter('city', e.target.value)}
                placeholder="Search by city, area..."
                className="flex-1 outline-none text-sm text-slate-900 bg-transparent"
                data-testid="search-city-input"
              />
            </div>
            <select value={filters.sort} onChange={e => updateFilter('sort', e.target.value)}
              className="px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none bg-white text-slate-700 hidden sm:block" data-testid="sort-select">
              <option value="newest">Newest First</option>
              <option value="rent_asc">Rent: Low to High</option>
              <option value="rent_desc">Rent: High to Low</option>
            </select>
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all lg:hidden ${filtersOpen ? 'bg-blue-700 text-white border-blue-700' : 'border-slate-200 text-slate-700 bg-white'}`}
              data-testid="toggle-filters"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Filter Sidebar - Desktop */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 sticky top-20">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-slate-900">Filters</h3>
                {activeFiltersCount > 0 && (
                  <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">{activeFiltersCount}</span>
                )}
              </div>
              <FilterPanel />
            </div>
          </aside>

          {/* Mobile Filters */}
          {filtersOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-black/50" onClick={() => setFiltersOpen(false)}></div>
              <div className="absolute right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto p-5">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-bold text-slate-900">Filters</h3>
                  <button onClick={() => setFiltersOpen(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                </div>
                <FilterPanel />
                <button onClick={() => setFiltersOpen(false)} className="w-full mt-5 bg-blue-700 text-white py-3 rounded-xl font-semibold text-sm">Apply Filters</button>
              </div>
            </div>
          )}

          {/* Results */}
          <div className="flex-1 min-w-0">
            {/* SEO H1 — contextual heading describing current search */}
            <h1 className="text-lg sm:text-xl font-bold text-slate-900 mb-1" style={{fontFamily:'Outfit,sans-serif'}} data-testid="search-h1">
              {seoTitle.replace(' | Rent Mitra', '')}
            </h1>
            <p className="text-slate-500 text-sm mb-4">
              {!loading && total > 0
                ? `${total.toLocaleString('en-IN')} verified listings${filters.city ? ` in ${filters.city}` : ' across India'} — direct owner contact, zero brokerage.`
                : `Verified rental listings${filters.city ? ` in ${filters.city}` : ' across India'} from owners and brokers. Search by BHK, budget, furnishing, and more.`}
            </p>
            <div className="flex items-center justify-between mb-5">
              <p className="text-slate-600 text-sm" data-testid="results-count">
                {loading ? 'Searching...' : `${total} properties found`}
              </p>
              <div className="flex items-center gap-2">
                <select value={filters.sort} onChange={e => updateFilter('sort', e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-xl text-sm outline-none bg-white text-slate-700 sm:hidden" data-testid="sort-select-mobile">
                  <option value="newest">Newest First</option>
                  <option value="rent_asc">Rent: Low to High</option>
                  <option value="rent_desc">Rent: High to Low</option>
                </select>
                {/* View toggle */}
                <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-0.5">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
                    title="Grid View"
                    data-testid="view-grid-btn"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'map' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
                    title="Map View"
                    data-testid="view-map-btn"
                  >
                    <Map className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {viewMode === 'map' && <PropertyMap filters={filters} />}

            {viewMode !== 'map' && loading && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            )}

            {viewMode !== 'map' && !loading && properties.length === 0 && (
              <div className="text-center py-20" data-testid="no-results">
                <div className="text-4xl mb-4">🏘️</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">No properties found</h3>
                <p className="text-slate-500 mb-5">Try adjusting your filters or search in a different area.</p>
                <button onClick={clearFilters} className="text-blue-700 font-semibold text-sm hover:underline">Clear all filters</button>
              </div>
            )}

            {viewMode !== 'map' && !loading && properties.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="properties-grid">
                  {properties.map(p => <PropertyCard key={p.property_id} property={p} isSaved={isSaved(p.property_id)} onToggleSave={handleToggleSave} />)}
                </div>
            )}
                {/* Pagination */}
                {pages > 1 && viewMode !== 'map' && !loading && (
                  <div className="flex items-center justify-center gap-3 mt-10">
                    <button disabled={page === 1} onClick={() => fetchProperties(page - 1)}
                      className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-slate-50 transition-colors" data-testid="prev-page">
                      <ChevronLeft className="w-4 h-4" />Prev
                    </button>
                    <span className="text-sm text-slate-600 font-medium">Page {page} of {pages}</span>
                    <button disabled={page === pages} onClick={() => fetchProperties(page + 1)}
                      className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-slate-50 transition-colors" data-testid="next-page">
                      Next<ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
